<!-- FieldPerformanceDashboard.vue - Dashboard de rendimiento de campo -->
<template>
  <div class="field-performance-dashboard">
    <!-- KPIs principales -->
    <v-row class="mb-4">
      <v-col cols="3">
        <v-card class="text-center pa-3" color="primary" variant="flat">
          <v-card-text class="text-white">
            <v-icon size="32" class="mb-2">mdi-chart-line</v-icon>
            <div class="text-h4">{{ kpis.efficiency }}%</div>
            <div class="text-body-2">Eficiencia</div>
            <v-chip
              size="small"
              :color="getTrendColor(kpis.efficiencyTrend)"
              class="mt-1"
            >
              <v-icon start size="14">{{ getTrendIcon(kpis.efficiencyTrend) }}</v-icon>
              {{ Math.abs(kpis.efficiencyTrend) }}%
            </v-chip>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="3">
        <v-card class="text-center pa-3" color="success" variant="flat">
          <v-card-text class="text-white">
            <v-icon size="32" class="mb-2">mdi-check-circle</v-icon>
            <div class="text-h4">{{ kpis.completedTasks }}</div>
            <div class="text-body-2">Tareas Completadas</div>
            <v-chip
              size="small"
              color="white"
              text-color="success"
              class="mt-1"
            >
              Hoy: {{ kpis.todayCompleted }}
            </v-chip>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="3">
        <v-card class="text-center pa-3" color="info" variant="flat">
          <v-card-text class="text-white">
            <v-icon size="32" class="mb-2">mdi-clock</v-icon>
            <div class="text-h4">{{ kpis.avgResponseTime }}h</div>
            <div class="text-body-2">Tiempo Promedio</div>
            <v-chip
              size="small"
              :color="getTrendColor(kpis.responseTimeTrend)"
              class="mt-1"
            >
              <v-icon start size="14">{{ getTrendIcon(kpis.responseTimeTrend) }}</v-icon>
              {{ Math.abs(kpis.responseTimeTrend) }}h
            </v-chip>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="3">
        <v-card class="text-center pa-3" color="warning" variant="flat">
          <v-card-text class="text-white">
            <v-icon size="32" class="mb-2">mdi-account-group</v-icon>
            <div class="text-h4">{{ kpis.activeTeams }}</div>
            <div class="text-body-2">Equipos Activos</div>
            <v-chip
              size="small"
              color="white"
              text-color="warning"
              class="mt-1"
            >
              {{ kpis.totalTeams }} total
            </v-chip>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Gráficos de rendimiento -->
    <v-row class="mb-4">
      <v-col cols="8">
        <v-card variant="outlined">
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-chart-timeline-variant</v-icon>
            Tendencias de Rendimiento
            <v-spacer></v-spacer>
            <v-btn-toggle
              v-model="chartPeriod"
              density="compact"
              mandatory
            >
              <v-btn value="day" size="small">Día</v-btn>
              <v-btn value="week" size="small">Semana</v-btn>
              <v-btn value="month" size="small">Mes</v-btn>
            </v-btn-toggle>
          </v-card-title>
          <v-card-text>
            <div class="chart-container">
              <!-- Placeholder para gráfico de líneas -->
              <div class="chart-placeholder">
                <v-icon size="48" color="grey-lighten-1">mdi-chart-line</v-icon>
                <div class="text-body-2 text-medium-emphasis mt-2">
                  Gráfico de tendencias por {{ chartPeriod === 'day' ? 'horas' : chartPeriod === 'week' ? 'días' : 'semanas' }}
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="4">
        <v-card variant="outlined">
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-chart-donut</v-icon>
            Distribución de Tareas
          </v-card-title>
          <v-card-text>
            <div class="task-distribution">
              <div
                v-for="category in taskDistribution"
                :key="category.name"
                class="distribution-item mb-3"
              >
                <div class="d-flex justify-space-between mb-1">
                  <span class="text-body-2">{{ category.name }}</span>
                  <span class="text-caption">{{ category.percentage }}%</span>
                </div>
                <v-progress-linear
                  :model-value="category.percentage"
                  :color="category.color"
                  height="8"
                  rounded
                ></v-progress-linear>
                <div class="text-caption text-medium-emphasis">
                  {{ category.count }} tareas
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Rendimiento por equipo -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card variant="outlined">
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-account-group-outline</v-icon>
            Rendimiento por Equipo
            <v-spacer></v-spacer>
            <v-text-field
              v-model="teamSearch"
              density="compact"
              placeholder="Buscar equipo..."
              prepend-inner-icon="mdi-magnify"
              hide-details
              style="max-width: 200px;"
            ></v-text-field>
          </v-card-title>
          <v-card-text>
            <div class="teams-performance">
              <v-data-table
                :headers="teamHeaders"
                :items="filteredTeamPerformance"
                :items-per-page="10"
                item-value="id"
                class="elevation-0"
              >
                <template v-slot:item.name="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar
                      :color="getTeamColor(item.name)"
                      size="32"
                      class="mr-3"
                    >
                      <span class="text-white text-caption">
                        {{ getTeamInitials(item.name) }}
                      </span>
                    </v-avatar>
                    <div>
                      <div class="text-body-2 font-weight-medium">
                        {{ item.name }}
                      </div>
                      <div class="text-caption text-medium-emphasis">
                        {{ item.leader }}
                      </div>
                    </div>
                  </div>
                </template>

                <template v-slot:item.efficiency="{ item }">
                  <div class="d-flex align-center">
                    <v-progress-circular
                      :model-value="item.efficiency"
                      :color="getEfficiencyColor(item.efficiency)"
                      size="24"
                      width="3"
                      class="mr-2"
                    ></v-progress-circular>
                    <span>{{ item.efficiency }}%</span>
                  </div>
                </template>

                <template v-slot:item.tasks_completed="{ item }">
                  <v-chip
                    :color="getCompletionColor(item.tasks_completed)"
                    size="small"
                  >
                    {{ item.tasks_completed }}
                  </v-chip>
                </template>

                <template v-slot:item.avg_time="{ item }">
                  <div class="text-body-2">{{ item.avg_time }}h</div>
                </template>

                <template v-slot:item.quality_score="{ item }">
                  <div class="d-flex align-center">
                    <v-rating
                      :model-value="item.quality_score / 2"
                      half-increments
                      readonly
                      size="small"
                      density="compact"
                      color="warning"
                      class="mr-2"
                    ></v-rating>
                    <span class="text-caption">{{ item.quality_score }}/10</span>
                  </div>
                </template>

                <template v-slot:item.status="{ item }">
                  <v-chip
                    :color="getStatusColor(item.status)"
                    size="small"
                    :prepend-icon="getStatusIcon(item.status)"
                  >
                    {{ getStatusText(item.status) }}
                  </v-chip>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn
                    icon="mdi-chart-box-outline"
                    size="small"
                    variant="text"
                    @click="viewTeamDetails(item)"
                  ></v-btn>
                  <v-btn
                    icon="mdi-account-details"
                    size="small"
                    variant="text"
                    @click="viewTeamMembers(item)"
                  ></v-btn>
                </template>
              </v-data-table>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Alertas de rendimiento -->
    <v-row>
      <v-col cols="12">
        <v-card variant="outlined">
          <v-card-title class="d-flex align-center">
            <v-icon class="mr-2">mdi-alert-outline</v-icon>
            Alertas de Rendimiento
          </v-card-title>
          <v-card-text>
            <div v-if="performanceAlerts.length === 0" class="text-center py-4">
              <v-icon size="48" color="success">mdi-check-circle</v-icon>
              <div class="text-body-1 mt-2">No hay alertas de rendimiento</div>
              <div class="text-caption text-medium-emphasis">
                Todos los equipos están funcionando dentro de los parámetros normales
              </div>
            </div>
            
            <div v-else class="alerts-list">
              <v-alert
                v-for="alert in performanceAlerts"
                :key="alert.id"
                :type="alert.severity"
                :title="alert.title"
                :text="alert.description"
                class="mb-2"
                variant="tonal"
              >
                <template v-slot:append>
                  <v-btn
                    size="small"
                    variant="text"
                    @click="resolveAlert(alert)"
                  >
                    Resolver
                  </v-btn>
                </template>
              </v-alert>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Botones de acción -->
    <div class="mt-4 d-flex justify-end gap-2">
      <v-btn
        color="info"
        prepend-icon="mdi-download"
        @click="exportReport"
      >
        Exportar Reporte
      </v-btn>
      <v-btn
        color="primary"
        prepend-icon="mdi-refresh"
        @click="refreshData"
      >
        Actualizar
      </v-btn>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  performanceData: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits([
  'export-report',
  'refresh-data'
])

// Reactive data
const chartPeriod = ref('day')
const teamSearch = ref('')

// Mock data (en producción vendría de props)
const kpis = ref({
  efficiency: 87,
  efficiencyTrend: 5.2,
  completedTasks: 142,
  todayCompleted: 23,
  avgResponseTime: 2.4,
  responseTimeTrend: -0.3,
  activeTeams: 8,
  totalTeams: 12
})

const taskDistribution = ref([
  { name: 'Mantenimiento', count: 45, percentage: 35, color: 'primary' },
  { name: 'Inspección', count: 38, percentage: 30, color: 'info' },
  { name: 'Reparación', count: 25, percentage: 20, color: 'warning' },
  { name: 'Emergencia', count: 19, percentage: 15, color: 'error' }
])

const teamPerformance = ref([
  {
    id: 1,
    name: 'Equipo Alpha',
    leader: 'Juan Pérez',
    efficiency: 92,
    tasks_completed: 28,
    avg_time: 2.1,
    quality_score: 9.2,
    status: 'active'
  },
  {
    id: 2,
    name: 'Equipo Beta',
    leader: 'María González',
    efficiency: 88,
    tasks_completed: 25,
    avg_time: 2.8,
    quality_score: 8.7,
    status: 'active'
  },
  {
    id: 3,
    name: 'Equipo Gamma',
    leader: 'Carlos López',
    efficiency: 75,
    tasks_completed: 18,
    avg_time: 3.2,
    quality_score: 7.5,
    status: 'warning'
  },
  {
    id: 4,
    name: 'Equipo Delta',
    leader: 'Ana Martínez',
    efficiency: 94,
    tasks_completed: 31,
    avg_time: 1.9,
    quality_score: 9.5,
    status: 'active'
  }
])

const performanceAlerts = ref([
  {
    id: 1,
    severity: 'warning',
    title: 'Rendimiento bajo en Equipo Gamma',
    description: 'El equipo ha mostrado una eficiencia del 75% en las últimas 24 horas, por debajo del objetivo del 80%.'
  },
  {
    id: 2,
    severity: 'info',
    title: 'Tiempo de respuesta elevado',
    description: 'El tiempo promedio de respuesta ha aumentado 15% en las últimas 4 horas.'
  }
])

// Table headers
const teamHeaders = [
  { title: 'Equipo', key: 'name', sortable: true },
  { title: 'Eficiencia', key: 'efficiency', sortable: true },
  { title: 'Tareas', key: 'tasks_completed', sortable: true },
  { title: 'Tiempo Promedio', key: 'avg_time', sortable: true },
  { title: 'Calidad', key: 'quality_score', sortable: true },
  { title: 'Estado', key: 'status', sortable: true },
  { title: 'Acciones', key: 'actions', sortable: false }
]

// Computed properties
const filteredTeamPerformance = computed(() => {
  if (!teamSearch.value) return teamPerformance.value
  
  return teamPerformance.value.filter(team =>
    team.name.toLowerCase().includes(teamSearch.value.toLowerCase()) ||
    team.leader.toLowerCase().includes(teamSearch.value.toLowerCase())
  )
})

// Methods
const getTrendColor = (trend) => {
  return trend >= 0 ? 'white' : 'white'
}

const getTrendIcon = (trend) => {
  return trend >= 0 ? 'mdi-trending-up' : 'mdi-trending-down'
}

const getTeamColor = (name) => {
  const colors = ['primary', 'info', 'success', 'warning', 'error', 'purple']
  const hash = name.split('').reduce((a, b) => a + b.charCodeAt(0), 0)
  return colors[hash % colors.length]
}

const getTeamInitials = (name) => {
  return name.split(' ').map(n => n[0]).join('')
}

const getEfficiencyColor = (efficiency) => {
  if (efficiency >= 90) return 'success'
  if (efficiency >= 80) return 'info'
  if (efficiency >= 70) return 'warning'
  return 'error'
}

const getCompletionColor = (count) => {
  if (count >= 25) return 'success'
  if (count >= 15) return 'info'
  if (count >= 10) return 'warning'
  return 'error'
}

const getStatusColor = (status) => {
  const colors = {
    active: 'success',
    warning: 'warning',
    inactive: 'grey',
    critical: 'error'
  }
  return colors[status] || 'grey'
}

const getStatusIcon = (status) => {
  const icons = {
    active: 'mdi-check-circle',
    warning: 'mdi-alert-circle',
    inactive: 'mdi-pause-circle',
    critical: 'mdi-close-circle'
  }
  return icons[status] || 'mdi-help-circle'
}

const getStatusText = (status) => {
  const texts = {
    active: 'Activo',
    warning: 'Alerta',
    inactive: 'Inactivo',
    critical: 'Crítico'
  }
  return texts[status] || status
}

// Event handlers
const viewTeamDetails = (team) => {
  console.log('📊 Ver detalles del equipo:', team.name)
  // Implementar vista detallada
}

const viewTeamMembers = (team) => {
  console.log('👥 Ver miembros del equipo:', team.name)
  // Implementar vista de miembros
}

const resolveAlert = (alert) => {
  console.log('✅ Resolver alerta:', alert.title)
  // Implementar resolución de alerta
}

const exportReport = () => {
  console.log('📄 Exportar reporte de rendimiento')
  emit('export-report')
}

const refreshData = () => {
  console.log('🔄 Actualizar datos de rendimiento')
  emit('refresh-data')
}
</script>

<style scoped>
.field-performance-dashboard {
  height: 100%;
}

.chart-container {
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-placeholder {
  text-align: center;
}

.distribution-item {
  padding: 8px 0;
}

.teams-performance {
  min-height: 400px;
}

.alerts-list {
  max-height: 300px;
  overflow-y: auto;
}

.gap-2 > * + * {
  margin-left: 8px;
}
</style>
